import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers, Plus, Recycle, BarChart2 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgCard: 'var(--color-bg-card)', border: 'var(--color-border)',
  amber: 'var(--color-primary)', green: '#2D6A2F', text: 'var(--color-text)',
  muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}

interface SoilAmendment {
  id: string
  name: string
  amendment_type: string
  supplier: string
  quantity_kg: number
  unit_cost_usd: number | null
  application_date: string | null
  location_description: string
  nitrogen_pct: number | null
  phosphorus_pct: number | null
  potassium_pct: number | null
  notes: string
  created_at: string
}

interface CompostBatch {
  id: string
  batch_name: string
  start_date: string
  ready_date: string | null
  status: 'active' | 'curing' | 'ready' | 'used'
  input_materials: string
  estimated_yield_kg: number | null
  actual_yield_kg: number | null
  location: string
  temperature_c: number | null
  notes: string
  created_at: string
}

const AMENDMENT_TYPES = [
  'Compost', 'Manure', 'Blood Meal', 'Bone Meal', 'Fish Emulsion',
  'Kelp Meal', 'Worm Castings', 'Lime', 'Sulfur', 'Rock Phosphate',
  'Greensand', 'Biochar', 'Mulch', 'Cover Crop', 'Other',
]

const COMPOST_STATUS_COLORS: Record<string, string> = {
  active: C.amber, curing: '#60A5FA', ready: '#4ADE80', used: C.muted,
}

export function SoilPage() {
  const chartC = useChartColors()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<'amendments' | 'compost'>('amendments')
  const [showAmendForm, setShowAmendForm] = useState(false)
  const [showCompostForm, setShowCompostForm] = useState(false)

  const [amendForm, setAmendForm] = useState({
    name: '', amendment_type: '', supplier: '', quantity_kg: '',
    unit_cost_usd: '', application_date: '', location_description: '',
    nitrogen_pct: '', phosphorus_pct: '', potassium_pct: '', notes: '',
  })
  const [compostForm, setCompostForm] = useState({
    batch_name: '', start_date: '', ready_date: '', status: 'active',
    input_materials: '', estimated_yield_kg: '', location: '', notes: '',
  })

  const { data: amendments = [], isLoading: loadingA } = useQuery<SoilAmendment[]>({
    queryKey: ['soil-amendments'],
    queryFn: () => api.get('/soil/amendments').then(r => r.data),
  })

  const { data: composts = [], isLoading: loadingC } = useQuery<CompostBatch[]>({
    queryKey: ['compost-batches'],
    queryFn: () => api.get('/soil/compost').then(r => r.data),
  })

  const createAmendment = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/soil/amendments', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['soil-amendments'] }); setShowAmendForm(false) },
  })

  const createCompost = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/soil/compost', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['compost-batches'] }); setShowCompostForm(false) },
  })

  const label: React.CSSProperties = { fontSize: 11, color: C.muted, marginBottom: 4, display: 'block' }
  const input: React.CSSProperties = { width: '100%', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', color: C.text, fontSize: 13 }
  const btnPrimary: React.CSSProperties = { padding: '8px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: C.amber, color: 'var(--color-bg)' }
  const btnGhost: React.CSSProperties = { padding: '8px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, color: C.muted, background: 'transparent' }

  const totalAmended = amendments.reduce((s, a) => s + a.quantity_kg, 0)
  const readyCompost = composts.filter(c => c.status === 'ready')
  const activeCompost = composts.filter(c => c.status === 'active' || c.status === 'curing')

  return (
    <main id="main-content" style={{ padding: '28px 32px', maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Soil & Compost</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
            Track soil amendments, fertilizer applications, and compost batches
          </p>
        </div>
        <button style={btnPrimary} onClick={() => activeTab === 'amendments' ? setShowAmendForm(true) : setShowCompostForm(true)}>
          <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          {activeTab === 'amendments' ? 'Add Amendment' : 'New Compost Batch'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total applied (kg)', value: totalAmended.toFixed(1), icon: Layers, color: C.amber },
          { label: 'Amendment types', value: new Set(amendments.map(a => a.amendment_type)).size, icon: BarChart2, color: C.amber },
          { label: 'Active batches', value: activeCompost.length, icon: Recycle, color: '#60A5FA' },
          { label: 'Ready compost', value: readyCompost.length, icon: Recycle, color: '#4ADE80' },
        ].map(({ label: l, value, icon: Icon, color }) => (
          <div key={l} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Icon size={14} color={color} />
              <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 3, width: 'fit-content' }}>
        {(['amendments', 'compost'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            background: activeTab === tab ? 'rgba(255,255,255,0.1)' : 'transparent',
            color: activeTab === tab ? C.text : C.muted,
          }}>
            {tab === 'amendments' ? 'Amendments' : 'Compost Batches'}
          </button>
        ))}
      </div>

      {/* AMENDMENTS TAB */}
      {activeTab === 'amendments' && (
        <>
          {showAmendForm && (
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: C.text }}>New Soil Amendment</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={label}>Product Name</label>
                  <input style={input} placeholder="e.g. Down to Earth Organic Mix" value={amendForm.name} onChange={e => setAmendForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Amendment Type</label>
                  <select style={input} value={amendForm.amendment_type} onChange={e => setAmendForm(f => ({ ...f, amendment_type: e.target.value }))}>
                    <option value="">Select type…</option>
                    {AMENDMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Supplier</label>
                  <input style={input} value={amendForm.supplier} onChange={e => setAmendForm(f => ({ ...f, supplier: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Quantity Applied (kg)</label>
                  <input type="number" style={input} value={amendForm.quantity_kg} onChange={e => setAmendForm(f => ({ ...f, quantity_kg: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Unit Cost (USD)</label>
                  <input type="number" step="0.01" style={input} value={amendForm.unit_cost_usd} onChange={e => setAmendForm(f => ({ ...f, unit_cost_usd: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Application Date</label>
                  <input type="date" style={input} value={amendForm.application_date} onChange={e => setAmendForm(f => ({ ...f, application_date: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>N % (Nitrogen)</label>
                  <input type="number" step="0.1" style={input} value={amendForm.nitrogen_pct} onChange={e => setAmendForm(f => ({ ...f, nitrogen_pct: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>P % (Phosphorus)</label>
                  <input type="number" step="0.1" style={input} value={amendForm.phosphorus_pct} onChange={e => setAmendForm(f => ({ ...f, phosphorus_pct: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>K % (Potassium)</label>
                  <input type="number" step="0.1" style={input} value={amendForm.potassium_pct} onChange={e => setAmendForm(f => ({ ...f, potassium_pct: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Location / Zone</label>
                  <input style={input} placeholder="e.g. South grove, Bed 3" value={amendForm.location_description} onChange={e => setAmendForm(f => ({ ...f, location_description: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Notes</label>
                <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} value={amendForm.notes} onChange={e => setAmendForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnPrimary}
                  onClick={() => createAmendment.mutate({ ...amendForm, quantity_kg: parseFloat(amendForm.quantity_kg) || 0, unit_cost_usd: amendForm.unit_cost_usd ? parseFloat(amendForm.unit_cost_usd) : null, nitrogen_pct: amendForm.nitrogen_pct ? parseFloat(amendForm.nitrogen_pct) : null, phosphorus_pct: amendForm.phosphorus_pct ? parseFloat(amendForm.phosphorus_pct) : null, potassium_pct: amendForm.potassium_pct ? parseFloat(amendForm.potassium_pct) : null })}
                  disabled={!amendForm.name || !amendForm.amendment_type}>
                  {createAmendment.isPending ? 'Saving…' : 'Save Amendment'}
                </button>
                <button style={btnGhost} onClick={() => setShowAmendForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {loadingA && <p style={{ color: C.muted }}>Loading…</p>}
          {!loadingA && amendments.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
              <Layers size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>No amendments logged yet. Record your first fertilizer or compost application.</p>
            </div>
          )}

          {amendments.length > 0 && (() => {
            const npkData = amendments
              .filter(a => a.nitrogen_pct != null || a.phosphorus_pct != null || a.potassium_pct != null)
              .map(a => ({
                name: a.name.length > 14 ? a.name.slice(0, 13) + '…' : a.name,
                N: a.nitrogen_pct ?? 0,
                P: a.phosphorus_pct ?? 0,
                K: a.potassium_pct ?? 0,
              }))
            return (
              <>
                {npkData.length > 0 && (
                  <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>N-P-K Content by Amendment</div>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>Nitrogen / Phosphorus / Potassium percentage</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={npkData} barCategoryGap="30%" barGap={2}>
                        <XAxis dataKey="name" tick={{ fill: chartC.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: chartC.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} width={36} />
                        <Tooltip
                          contentStyle={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                          formatter={(val: number, name: string) => [`${val}%`, name === 'N' ? 'Nitrogen' : name === 'P' ? 'Phosphorus' : 'Potassium']}
                          labelStyle={{ color: C.text, fontWeight: 600, marginBottom: 4 }}
                        />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: C.muted }}
                          formatter={(val: string) => val === 'N' ? 'Nitrogen' : val === 'P' ? 'Phosphorus' : 'Potassium'} />
                        <Bar dataKey="N" fill="#4CAF50" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="P" fill="#FF9800" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="K" fill="#9C27B0" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {['Product', 'Type', 'Quantity (kg)', 'N-P-K', 'Applied', 'Location', 'Cost'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {amendments.map(a => (
                    <tr key={a.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '12px 14px', color: C.text, fontWeight: 500, fontSize: 13 }}>{a.name}</td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>{a.amendment_type}</td>
                      <td style={{ padding: '12px 14px', color: C.text, fontSize: 13, fontWeight: 600 }}>{a.quantity_kg.toFixed(1)}</td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>
                        {a.nitrogen_pct != null && a.phosphorus_pct != null && a.potassium_pct != null
                          ? `${a.nitrogen_pct}-${a.phosphorus_pct}-${a.potassium_pct}` : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>
                        {a.application_date ? new Date(a.application_date).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>{a.location_description || '—'}</td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12 }}>
                        {a.unit_cost_usd != null ? `$${(a.unit_cost_usd * a.quantity_kg).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                  </table>
                </div>
              </>
            )
          })()}
        </>
      )}

      {/* COMPOST TAB */}
      {activeTab === 'compost' && (
        <>
          {showCompostForm && (
            <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: C.text }}>New Compost Batch</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={label}>Batch Name</label>
                  <input style={input} placeholder="e.g. Spring 2024 Batch 1" value={compostForm.batch_name} onChange={e => setCompostForm(f => ({ ...f, batch_name: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Status</label>
                  <select style={input} value={compostForm.status} onChange={e => setCompostForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="active">Active (building)</option>
                    <option value="curing">Curing</option>
                    <option value="ready">Ready to use</option>
                    <option value="used">Used / Depleted</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Start Date</label>
                  <input type="date" style={input} value={compostForm.start_date} onChange={e => setCompostForm(f => ({ ...f, start_date: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Expected Ready Date</label>
                  <input type="date" style={input} value={compostForm.ready_date} onChange={e => setCompostForm(f => ({ ...f, ready_date: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Estimated Yield (kg)</label>
                  <input type="number" style={input} value={compostForm.estimated_yield_kg} onChange={e => setCompostForm(f => ({ ...f, estimated_yield_kg: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Location</label>
                  <input style={input} placeholder="e.g. Compost bay 1" value={compostForm.location} onChange={e => setCompostForm(f => ({ ...f, location: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={label}>Input Materials (what went in)</label>
                <textarea style={{ ...input, minHeight: 60, resize: 'vertical' }} placeholder="e.g. Mango trimmings, chicken manure, wood chips, kitchen scraps" value={compostForm.input_materials} onChange={e => setCompostForm(f => ({ ...f, input_materials: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnPrimary}
                  onClick={() => createCompost.mutate({ ...compostForm, estimated_yield_kg: compostForm.estimated_yield_kg ? parseFloat(compostForm.estimated_yield_kg) : null })}
                  disabled={!compostForm.batch_name}>
                  {createCompost.isPending ? 'Saving…' : 'Create Batch'}
                </button>
                <button style={btnGhost} onClick={() => setShowCompostForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {loadingC && <p style={{ color: C.muted }}>Loading…</p>}
          {!loadingC && composts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
              <Recycle size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>No compost batches yet. Start tracking your first batch.</p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {composts.map(batch => (
              <div key={batch.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{batch.batch_name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: COMPOST_STATUS_COLORS[batch.status], background: `${COMPOST_STATUS_COLORS[batch.status]}22`, padding: '2px 8px', borderRadius: 20 }}>
                    {batch.status.toUpperCase()}
                  </span>
                </div>
                {batch.input_materials && (
                  <p style={{ fontSize: 12, color: C.muted, margin: '0 0 8px' }}>{batch.input_materials.slice(0, 80)}{batch.input_materials.length > 80 ? '…' : ''}</p>
                )}
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.faint }}>
                  <span>Started: {new Date(batch.start_date).toLocaleDateString()}</span>
                  {batch.ready_date && <span>Ready: {new Date(batch.ready_date).toLocaleDateString()}</span>}
                </div>
                {batch.estimated_yield_kg && (
                  <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
                    Est. yield: <strong style={{ color: C.text }}>{batch.estimated_yield_kg} kg</strong>
                    {batch.actual_yield_kg && <> → actual: <strong style={{ color: '#4ADE80' }}>{batch.actual_yield_kg} kg</strong></>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </main>
  )
}
